import {
  authorityStateForQuestion,
  isCommonSectionQuestion,
} from "./authorityProgress";
import { isCanonicalConsentQuestion } from "./consentQuestionClassification";
import { TREATMENT_RUNTIME_STATE } from "./treatmentApiConstants";

export const CUSTOM_PROGRAM_STAGE = Object.freeze({
  content: "custom_questions_and_sections",
  programs: "programs",
  consents: "consents",
});

const STAGE_ORDER = Object.freeze({
  [CUSTOM_PROGRAM_STAGE.content]: 1,
  [CUSTOM_PROGRAM_STAGE.programs]: 2,
  [CUSTOM_PROGRAM_STAGE.consents]: 3,
});

const stageForQuestion = (question) => {
  if (!question) return null;

  const authorityState = authorityStateForQuestion(question);
  if (
    authorityState === TREATMENT_RUNTIME_STATE.consents
    && isCanonicalConsentQuestion(question)
  ) {
    return CUSTOM_PROGRAM_STAGE.consents;
  }

  if (
    question.question_type === "state_routing"
    || authorityState === TREATMENT_RUNTIME_STATE.routing
    || authorityState === TREATMENT_RUNTIME_STATE.commonScreening
    || isCommonSectionQuestion(question)
  ) {
    return CUSTOM_PROGRAM_STAGE.content;
  }

  if (
    question.question_type === "matched_program_selection"
    || question.question_type === "treatment_screening_intro"
    || question.question_type === "labs"
    || question.question_type === "program_lab_checkout"
    || question.program_id
    || authorityState === TREATMENT_RUNTIME_STATE.potentialMatches
    || authorityState === TREATMENT_RUNTIME_STATE.treatmentScreening
    || authorityState === TREATMENT_RUNTIME_STATE.recommendations
    || authorityState === TREATMENT_RUNTIME_STATE.productSelection
  ) {
    return CUSTOM_PROGRAM_STAGE.programs;
  }

  return null;
};

const isAuthentication = (question) => (
  question?.question_type === "patient_authentication"
);

export const customProgramStageForStep = (step) => (
  stageForQuestion(step?.question || step?.questions?.[0] || null)
);

export const lastCustomProgramStepItemId = (step) => {
  const itemIds = Array.isArray(step?.itemIds) ? step.itemIds : [];
  return itemIds[itemIds.length - 1] || step?.id || null;
};

export const backTargetForPreviousTreatmentStep = ({
  previousVisibleState,
  previousTreatmentStep,
  stateForQuestion,
}) => (
  previousVisibleState === TREATMENT_RUNTIME_STATE.treatmentScreening
  && previousTreatmentStep
  // The screening intro is a local orientation page, not a released cursor.
  && previousTreatmentStep.question?.question_type !== "treatment_screening_intro"
  && stateForQuestion(previousTreatmentStep.question) === previousVisibleState
) ? lastCustomProgramStepItemId(previousTreatmentStep) : null;

/**
 * Resolve the authored predecessor for a Custom Program.
 *
 * The runtime may rebuild its visible projection when entering/leaving a
 * deduplicated Section or Consent boundary. Do not use the journey machine's
 * history for that case: history can contain the old occurrence that the
 * projection deliberately removed. Walk the current released projection and
 * respect the three Custom Program stages instead.
 */
export const previousCustomProgramStep = (steps, currentStepId) => {
  const visible = Array.isArray(steps) ? steps : [];
  const currentIndex = visible.findIndex((step) => step.id === currentStepId);
  if (currentIndex <= 0) return null;

  const currentStage = customProgramStageForStep(visible[currentIndex]);
  if (!currentStage) return visible[currentIndex - 1] || null;

  const currentOrder = STAGE_ORDER[currentStage];
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const candidate = visible[index];
    if (isAuthentication(candidate?.question)) return candidate;

    const candidateStage = customProgramStageForStep(candidate);
    if (candidateStage === currentStage) return candidate;

    // At a stage boundary, land on the last item of the immediately previous
    // Custom Program stage. Ignore any stale/later-stage occurrence that may
    // still be present in a transitional projection.
    if (
      candidateStage
      && STAGE_ORDER[candidateStage] < currentOrder
    ) {
      return candidate;
    }
  }

  return visible[0] || null;
};
